/**
 * Delivery Info Component
 * Handles dynamic visibility and delivery type based on variant inventory
 * Integrates with the theme's VariantSelects custom element
 */

document.addEventListener("DOMContentLoaded", function () {
    initializeDeliveryInfo();
});

/**
 * Initialize delivery info component
 */
function initializeDeliveryInfo() {
    const deliveryInfoElement = document.querySelector(
        ".delivery-info"
    );
    if (!deliveryInfoElement) return;

    // Find the variant-selects element (not sticky version)
    const variantSelects = document.querySelector(
        "variant-selects:not([data-sticky])"
    );
    if (!variantSelects) return;

    // Listen for variant changes on the variant-selects element
    variantSelects.addEventListener("change", function () {
        // Small delay to ensure currentVariant is updated
        setTimeout(function () {
            updateDeliveryInfoDisplay(variantSelects);
        }, 50);
    });

    // Initial check
    setTimeout(function () {
        updateDeliveryInfoDisplay(variantSelects);
    }, 100);
}

/**
 * Update delivery info display based on variant inventory
 * @param {HTMLElement} variantSelects - The variant-selects element
 */
function updateDeliveryInfoDisplay(variantSelects) {
    const deliveryInfoElement = document.querySelector(
        ".delivery-info"
    );
    if (!deliveryInfoElement) return;

    // Get currentVariant from the VariantSelects custom element
    const currentVariant = variantSelects.currentVariant;

    if (!currentVariant) {
        // No variant selected, hide delivery info
        deliveryInfoElement.style.display = "none";
        return;
    }

    // Get variant inventory data from data attribute
    let variantInventoryData = {};
    try {
        const inventoryDataStr =
            deliveryInfoElement.getAttribute(
                "data-variant-inventory"
            );
        if (inventoryDataStr) {
            variantInventoryData = JSON.parse(
                inventoryDataStr
            );
        }
    } catch (e) {
        console.error(
            "[Delivery Info] Error parsing variant inventory data:",
            e
        );
    }

    // Get inventory info for current variant
    const variantInfo =
        variantInventoryData[currentVariant.id];

    if (!variantInfo) {
        // Fallback to using currentVariant.available
        if (currentVariant.available) {
            deliveryInfoElement.style.display = "flex";
        } else {
            deliveryInfoElement.style.display = "none";
        }
        return;
    }

    // Check if this is a special streetwear product (essentials/denim tears/stussy + streetwear tag)
    const isSpecialStreetwear =
        deliveryInfoElement.getAttribute(
            "data-is-special-streetwear"
        ) === "true";

    // Check if this is specifically an essentials product
    const isEssentials =
        deliveryInfoElement.getAttribute(
            "data-is-essentials"
        ) === "true";

    // Check if this is an adidas tang or knit jacket product (adidas tang or knit jacket + streetwear tag)
    const isAdidasTang =
        deliveryInfoElement.getAttribute(
            "data-is-adidas-tang"
        ) === "true";

    // Check if this is a new chinese product (new chinese + streetwear tag)
    const isNewChinese =
        deliveryInfoElement.getAttribute(
            "data-is-new-chinese"
        ) === "true";

    // Check if this is a YS-01 (Yeezy Slide) product — no streetwear-tag gate
    const isYs01 =
        deliveryInfoElement.getAttribute(
            "data-is-ys01"
        ) === "true";

    // Determine delivery type based on inventory status
    let deliveryType = "standard";
    let shouldShow = false;

    // Check inventory quantity from our embedded data
    const inventoryQty =
        variantInfo.inventory_quantity || 0;
    const isAvailable = variantInfo.available;

    if (isYs01) {
        // YS-01: express (24-48h) if in stock, 1-5 days if out of stock
        if (inventoryQty > 0) {
            deliveryType = "express";
            shouldShow = true;
        } else if (isAvailable) {
            deliveryType = "special_1_5";
            shouldShow = true;
        } else {
            shouldShow = false;
        }
    } else if (isEssentials) {
        // Essentials: express (24-48h) if in stock, 1-7 days if out of stock
        if (inventoryQty > 0) {
            deliveryType = "express";
            shouldShow = true;
        } else if (isAvailable) {
            deliveryType = "special_1_7";
            shouldShow = true;
        } else {
            shouldShow = false;
        }
    } else if (isAdidasTang) {
        // Adidas Tang / Knit Jacket: express (24-48h) if in stock, 1-7 days if out of stock
        if (inventoryQty > 0) {
            deliveryType = "express";
            shouldShow = true;
        } else if (isAvailable) {
            deliveryType = "special_1_7";
            shouldShow = true;
        } else {
            shouldShow = false;
        }
    } else if (isNewChinese) {
        // New Chinese: express (24-48h) if in stock, 1-7 days if out of stock
        if (inventoryQty > 0) {
            deliveryType = "express";
            shouldShow = true;
        } else if (isAvailable) {
            deliveryType = "special_1_7";
            shouldShow = true;
        } else {
            shouldShow = false;
        }
    } else if (isSpecialStreetwear) {
        // Denim Tears / Stussy: 1-5 days if in stock, 1-10 days if out of stock
        if (inventoryQty > 0) {
            deliveryType = "special_1_5";
            shouldShow = true;
        } else if (isAvailable) {
            deliveryType = "special_2_10";
            shouldShow = true;
        } else {
            shouldShow = false;
        }
    } else if (inventoryQty > 0) {
        // Regular products with inventory: show express delivery (24-48h)
        deliveryType = "express";
        shouldShow = true;
    } else if (isAvailable) {
        // Regular products without inventory but available: show standard delivery (1-10 days)
        deliveryType = "standard";
        shouldShow = true;
    } else {
        // Not available at all: hide
        shouldShow = false;
    }

    // Update visibility
    if (shouldShow) {
        deliveryInfoElement.style.display = "flex";

        // Update delivery type class
        deliveryInfoElement.classList.remove(
            "delivery-info--express",
            "delivery-info--standard",
            "delivery-info--special",
            "delivery-info--special_1_5",
            "delivery-info--special_1_7",
            "delivery-info--special_2_7",
            "delivery-info--special_2_10"
        );
        deliveryInfoElement.classList.add(
            `delivery-info--${deliveryType}`
        );

        // Update data attribute
        deliveryInfoElement.setAttribute(
            "data-delivery-type",
            deliveryType
        );

        // Update text content
        const titleElement =
            deliveryInfoElement.querySelector(
                ".delivery-info__title"
            );
        if (
            titleElement &&
            window.theme &&
            window.theme.translations &&
            window.theme.translations.products
        ) {
            const translations =
                window.theme.translations.products.product
                    .shipping;
            if (
                deliveryType === "special_1_7" &&
                translations.delivery_1_7_days
            ) {
                titleElement.textContent =
                    translations.delivery_1_7_days;
            } else if (
                deliveryType === "special_1_7" &&
                translations.delivery_1_7_days
            ) {
                titleElement.textContent =
                    translations.delivery_1_7_days;
            } else if (
                deliveryType === "special_1_5" &&
                translations.delivery_1_5_days
            ) {
                titleElement.textContent =
                    translations.delivery_1_5_days;
            } else if (
                deliveryType === "special_2_7" &&
                translations.delivery_2_7_days
            ) {
                titleElement.textContent =
                    translations.delivery_2_7_days;
            } else if (
                deliveryType === "special_2_10" &&
                translations.delivery_2_10_days
            ) {
                titleElement.textContent =
                    translations.delivery_2_10_days;
            } else if (
                deliveryType === "express" &&
                translations.express_delivery
            ) {
                titleElement.textContent =
                    translations.express_delivery;
            } else if (
                deliveryType === "standard" &&
                translations.delivery_2_10_days
            ) {
                titleElement.textContent =
                    translations.delivery_2_10_days;
            }
        }
    } else {
        deliveryInfoElement.style.display = "none";
    }
}
